import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';
import { ArrowLeft } from 'lucide-react';

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-white" data-testid="work-experience-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link 
            to="/giving-back" 
            className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-medium mb-8 hover:gap-3 transition-all"
          >
            <ArrowLeft size={18} />
            Back to #GivingBack
          </Link>
          
          <span className="inline-block px-3 py-1 bg-[#faf9f7] text-[#d4782c] font-dm-sans font-semibold text-sm mb-4">
            2026
          </span>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-4">
            Work Experience Program
          </h1>
          <p className="text-xl md:text-2xl text-[#d4782c] font-dm-sans font-medium">
            #GivingBack 2026
          </p>
        </div>
      </div>
    </section>
  );
};

// Content Section Component
const ContentSection = ({ heading, children, bgColor = 'white' }) => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className={`py-12 md:py-16 ${bgColor === 'white' ? 'bg-white' : 'bg-[#faf9f7]'}`}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6">
            {heading}
          </h2>
          <div className="space-y-6 text-[#4a4744] leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-20 bg-[#faf9f7]">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#4a4744] mb-6">
            Interested in future #GivingBack initiatives?
          </p>
          <Link 
            to="/giving-back#get-involved"
            className="inline-block bg-[#1a1a1a] text-white px-8 py-4 font-dm-sans font-semibold hover:bg-[#333] transition-colors"
          >
            Get Involved
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function WorkExperiencePage() {
  return (
    <Layout>
      <SEO 
        title="Work Experience Program | #GivingBack 2026"
        path="/giving-back/work-experience-2026"
        description="A two-week placement giving Year 12 students from the AASMA network their first real experience of professional work, with genuine tasks, real responsibility, and a finished piece of work to call their own."
      />
      <HeroSection />
      
      <ContentSection heading="What is the Work Experience Program" bgColor="offwhite">
        <p>
          The Agilitie Work Experience Program gives Year 12 students their first real taste of professional work. Over two weeks, students join Agilitie as interns, take on genuine tasks, and finish with something they built themselves. This is not observation or work shadowing. It is real work, with real responsibility, of a kind that is hard to come by at their age.
        </p>
      </ContentSection>

      <ContentSection heading="Why It Matters" bgColor="white">
        <p>
          Most young people never see the inside of a working business before they leave school, and the ones who do are usually those who already have the connections. This program is for the students who do not. Reached through the AASMA network, they are bright and capable and simply need a door opened. Giving them real experience now, and a piece of work they can point to, is one of the most useful things we can offer at the very start of a career.
        </p>
      </ContentSection>

      <ContentSection heading="How It Works" bgColor="offwhite">
        <p>
          The program runs over two weeks and is built around a single real project: exploratory testing of live websites, the same discipline professional teams use before a release. Students plan their own approach, work largely on their own, and write up their findings and recommendations. Four short sessions bookend the fortnight, for onboarding, a deep dive, a mid-point check, and a final readout where each student presents their work. Everyone finishes with a structured report in their own name.
        </p>
      </ContentSection>

      <ContentSection heading="What They Take Away" bgColor="white">
        <p>
          By the end, each student has done genuine professional work, learned to organise themselves and to explain clearly what they found, and has two things to show for it: a report that carries their name, and a certificate they can put towards a scholarship or university application. More than that, they leave knowing they can do the work.
        </p>
      </ContentSection>

      <CTASection />
    </Layout>
  );
}
